import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const defaultTab = tabParam === 'signup' ? 'signup' : 'signin';
  
  const [isLoading, setIsLoading] = useState(false);
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpFullName, setSignUpFullName] = useState('');
  const [signUpUsername, setSignUpUsername] = useState('');
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate('/');
    return null;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn(signInEmail, signInPassword);

    if (error) {
      toast.error(error.message || 'Failed to sign in');
    } else {
      navigate('/');
    }

    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signUp(signUpEmail, signUpPassword, signUpFullName, signUpUsername);

    if (error) {
      // Check for specific error types
      const errorMessage = error.message?.toLowerCase() || '';
      const errorDetails = error.details?.toLowerCase() || '';
      
      // Check for our custom email error first
      if (errorMessage === 'email_already_exists' || errorDetails.includes('email already has an account')) {
        toast.error('This email already has an account.');
      }
      // Check for our custom username error
      else if (errorMessage === 'username_already_exists' || errorDetails.includes('username you have chosen is already in use')) {
        toast.error('The username you have chosen is already in use.');
      } 
      // Check for duplicate username from database
      else if (errorMessage.includes('duplicate key value violates unique constraint "profiles_username_key"') || 
          errorMessage.includes('duplicate key value violates unique constraint "public_profile_data_username_key"') ||
          (errorMessage.includes('username') && errorMessage.includes('already'))) {
        toast.error('The username you have chosen is already in use.');
      } 
      // Check for duplicate email - multiple possible error messages
      else if (errorMessage.includes('user already registered') || 
               errorMessage.includes('email address already') ||
               errorMessage.includes('duplicate') && errorMessage.includes('email') ||
               (errorMessage.includes('email') && errorMessage.includes('exists')) ||
               (errorMessage.includes('email') && errorMessage.includes('already'))) {
        toast.error('This email already has an account.');
      } 
      // Generic database error
      else if (errorMessage.includes('database error')) {
        toast.error('Unable to create account. The username or email may already be in use.');
      } 
      // Any other error
      else {
        toast.error(error.message || 'Failed to sign up');
      }
    } else {
      toast.success('A confirmation email has been sent to your email address. Please check your inbox and click the confirmation link to complete your registration.');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-24">
        <Card className="w-full max-w-md shadow-2xl border-gold/20 relative overflow-hidden">
          {/* Logo */}
          <div className="absolute top-6 left-6 z-10">
            <img 
              src="/lovable-uploads/a53f5ba2-203a-4732-899c-ff2777cb7d2c.png" 
              alt="Freeman & Crown Law Logo" 
              className="w-8 h-8"
            />
          </div>
          
          <CardHeader className="text-center pt-16 pb-6">
            <CardTitle className="text-3xl font-rockwell font-bold text-primary mb-2">
              Private Members Area
            </CardTitle>
            <CardDescription className="text-base">
              Access your account or join our private community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin" className="font-medium">Login</TabsTrigger>
                <TabsTrigger value="signup" className="font-medium">Join</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin" className="mt-0">
                <form onSubmit={handleSignIn} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="your@email.com"
                      value={signInEmail}
                      onChange={(e) => setSignInEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-sm font-medium">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="Enter your password"
                      value={signInPassword}
                      onChange={(e) => setSignInPassword(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold" disabled={isLoading}>
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup" className="mt-0">
                <form onSubmit={handleSignUp} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="text-sm font-medium">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={signUpFullName}
                      onChange={(e) => setSignUpFullName(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-username" className="text-sm font-medium">Username</Label>
                    <Input
                      id="signup-username"
                      type="text"
                      placeholder="johndoe"
                      value={signUpUsername}
                      onChange={(e) => setSignUpUsername(e.target.value.toLowerCase())}
                      required
                      minLength={3}
                      maxLength={20}
                      pattern="[a-z0-9_.]+"
                      title="Username can only contain lowercase letters, numbers, underscores, and periods"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="your@email.com"
                      value={signUpEmail}
                      onChange={(e) => setSignUpEmail(e.target.value)}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-sm font-medium">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Minimum 6 characters"
                      value={signUpPassword}
                      onChange={(e) => setSignUpPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-11"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold" disabled={isLoading}>
                    {isLoading ? 'Creating account...' : 'Join Private Membership'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;
